import {Route, BrowserRouter as Router, Routes} from 'react-router';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import {useTranslation} from 'react-i18next';
import {useEffect} from 'react';
import routePermissions, {RoutePermissions} from './constants/routePermissions';
import {requestPermissionAndGetToken} from './utils';
import {syncFcmToken} from './utils/fcmToken';
import TextEntryContextProvider from './context/TextEntryContextProvider';
import moment from 'moment';
import 'moment/locale/he';
import {CacheProvider} from '@emotion/react';
import {ThemeProvider} from '@mui/material';
import theme from './theme/theme';
import {prefixer} from 'stylis';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import SnackbarContextProvider from './context/SnackbarContextProvider';
import QueryProvider from './context/QueryProvider';
import GlobalLoader from './components/common/GlobalLoader';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import PublicForm from './pages/public-form';
import OnBoardingLayout from './components/OnBoardingLayout/OnBoardingLayout';

const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const ltrCache = createCache({
  key: 'mui',
});

function App() {
  const {i18n} = useTranslation();

  moment.locale(i18n.language === 'heb' ? 'he' : 'en');

  useEffect(() => {
    requestPermission();

    const lng = localStorage.getItem('currentLang') ?? 'heb';
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'heb' ? 'rtl' : 'ltr';

    // Tabs stay open for days: re-check the token when the tab comes back
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      await syncFcmToken(await requestPermissionAndGetToken(false));
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  async function requestPermission() {
    // Registers this browser for an already logged-in user (no re-login needed)
    await syncFcmToken(await requestPermissionAndGetToken());
  }

  return (
    <CacheProvider value={i18n.language === 'heb' ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <SnackbarContextProvider>
          <QueryProvider>
            <TextEntryContextProvider>
              <Router>
                <Routes>
                  <Route path="/" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/form/:formId" element={<PublicForm />} />

                  {routePermissions.map((route: RoutePermissions) => {
                    const isOnboarding = route.path === '/on-boarding';

                    return (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <ProtectedRoute
                            permission={route.permissions}
                            onlyNetworkOwner={route.path === '/on-boarding'}>
                            {isOnboarding ? (
                              <OnBoardingLayout>
                                {route.screen}
                              </OnBoardingLayout>
                            ) : (
                              <Layout>{route.screen}</Layout>
                            )}
                          </ProtectedRoute>
                        }
                      />
                    );
                  })}

                  <Route
                    path="/no-access"
                    element={
                      <div>
                        <h1>No Access To View Any Screen</h1>
                      </div>
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <div>
                        <h1>404 Page Not Found</h1>
                      </div>
                    }
                  />
                </Routes>
              </Router>

              <GlobalLoader />
            </TextEntryContextProvider>
          </QueryProvider>
        </SnackbarContextProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
